import React from "react";

const normalizeHref = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (v.toLowerCase().startsWith("wa:") || v.toLowerCase().startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (v.toLowerCase().startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;
  return `/${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal = /^https?:\/\//i.test(href);
  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const HeroSection2 = ({ config, style }) => {
  const align = (config.alignment || "center").toLowerCase();
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const justifyMap = { left: "justify-start", center: "justify-center", right: "justify-end" };

  const alignClass = alignMap[align] || "text-center";
  const justifyClass = justifyMap[align] || "justify-center";

  const tagEmoji = config.tagEmoji || "🚀";
  const tagText = config.tagText || "NOW AVAILABLE";

  const primaryText = (config.buttonText || "").trim();
  const primaryLink = (config.buttonLink || "").trim();
  const primaryDisabled = !!primaryText && !primaryLink;

  const secondaryText = (config.secondaryButtonText || "").trim();
  const secondaryLink = (config.secondaryButtonLink || "").trim();
  const secondaryDisabled = !!secondaryText && !secondaryLink;

  const bg = buildBg(style, "bg-gradient-to-r from-purple-900 via-blue-900 to-purple-900");

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`min-h-screen flex items-center justify-center text-white py-20 px-4 ${bg.className}`}
    >
      <div className={`container mx-auto max-w-6xl ${alignClass}`}>
        <div className="mb-6">
            <span
              style={{
                backgroundColor: style?.heroTagBg || undefined,
                borderColor: style?.heroTagBorder || undefined,
                color: style?.heroTagTextColor || undefined,
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${
                style?.heroTagBg || style?.heroTagBorder || style?.heroTagTextColor
                  ? "border"
                  : "bg-blue-500/20 text-blue-300"
              }`}
            >
              <span>{tagEmoji}</span>
              <span>{tagText}</span>
            </span>
          </div>

        <h1
          style={{ color: style?.titleColor || undefined }}
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
        >
          {config.title || "Your title"}
        </h1>

        <p
          style={{ color: style?.subtitleColor || undefined }}
          className={`text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl ${align === "center" ? "mx-auto" : ""
            }`}
        >
          {config.subtitle || "Your subtitle"}
        </p>

        <div className={`flex flex-col sm:flex-row gap-4 ${justifyClass} mb-16`}>
          {primaryText ? (
            <button
              disabled={primaryDisabled}
              onClick={() => !primaryDisabled && goToLink(primaryLink)}
              style={{
                backgroundColor: style?.primaryBtnBg || undefined,
                color: style?.primaryBtnText || undefined,
              }}
              className={`font-bold py-4 px-10 rounded-full text-lg transition-all duration-300 ${primaryDisabled
                ? "opacity-40 cursor-not-allowed"
                : style?.primaryBtnBg
                  ? "hover:opacity-90"
                  : "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:shadow-2xl hover:scale-105"
                }`}
            >
              {primaryText}
            </button>
          ) : null}

          {secondaryText ? (
            <button
              disabled={secondaryDisabled}
              onClick={() => !secondaryDisabled && goToLink(secondaryLink)}
              style={{
                borderColor: style?.secondaryBtnBorder || undefined,
                color: style?.secondaryBtnText || undefined,
              }}
              className={`border font-bold py-4 px-10 rounded-full text-lg transition-all duration-300 ${secondaryDisabled
                ? "opacity-40 cursor-not-allowed"
                : style?.secondaryBtnBorder || style?.secondaryBtnText
                  ? "hover:opacity-90"
                  : "bg-white/10 backdrop-blur-sm hover:bg-white/20 border-white/30 text-white"
                }`}
            >
              {secondaryText}
            </button>
          ) : null}
        </div>

        <div
          className={`relative max-w-4xl ${align === "center" ? "mx-auto": align === "right" ? "ml-auto" : "" }`}
        >
          {config.image ? (
            <img
              src={config.image}
              alt={config.imageAlt || "Hero"}
              className="rounded-3xl shadow-2xl border border-white/10 w-full"
            />
          ) : (
            <div className="rounded-3xl border border-white/10 w-full h-[340px] bg-white/10" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-purple-900/30 to-transparent rounded-3xl" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection2;
